import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import apiClient from "../../../../../services/apiClient";
import staffService from "../../../../../services/staffService";

const StaffRoleMaster = () => {
    const { user } = useSelector((state) => state.auth);
    const createdBy = user?.name || user?.Name || user?.userName || "SiteAdmin";

    const [categories, setCategories] = useState([]);
    const [categoryId, setCategoryId] = useState("");
    const [roles, setRoles] = useState([]);
    const [roleName, setRoleName] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadCategories = useCallback(async () => {
        try {
            const response = await apiClient.get("/vendor-management/vendor/GetCategories");
            const list = response.data?.isSuccess && Array.isArray(response.data?.data) ? response.data.data : [];
            setCategories(list);
            setCategoryId((current) => current || (list[0]?.categoryId ? String(list[0].categoryId) : ""));
        } catch (error) {
            console.error("Failed to load categories", error);
            toast.error("Failed to load categories");
        }
    }, []);

    const loadRoles = useCallback(async () => {
        if (!categoryId) {
            setRoles([]);
            return;
        }
        setLoading(true);
        try {
            const response = await staffService.getStaffRoles({
                categoryId: Number(categoryId)
            });
            setRoles(response.data?.isSuccess && Array.isArray(response.data?.data) ? response.data.data : []);
        } catch (error) {
            console.error("Failed to load roles", error);
            toast.error("Failed to load roles");
            setRoles([]);
        } finally {
            setLoading(false);
        }
    }, [categoryId]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    useEffect(() => {
        loadRoles();
    }, [loadRoles]);

    const handleAddRole = async (event) => {
        event.preventDefault();
        if (!categoryId) {
            toast.error("Select a category first");
            return;
        }
        if (!roleName.trim()) {
            toast.error("Enter a role name");
            return;
        }
        setSaving(true);
        try {
            const response = await staffService.createStaffRole({
                categoryId: Number(categoryId),
                roleName: roleName.trim(),
                createdBy
            });
            if (response.data?.isSuccess === false) {
                toast.error(response.data?.message || "Failed to add role");
                return;
            }
            toast.success("Role added");
            setRoleName("");
            loadRoles();
        } catch (error) {
            console.error("Failed to add role", error);
            toast.error(error.response?.data?.message || "Failed to add role");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs items={["Workforce Management", "Staff Roles"]} />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Staff Roles"
                subHeading="Pick a vendor category, then add the job titles that belong to it."
            />

            <div className="mt-6 flex flex-col gap-4">
                <label className="flex flex-col gap-1 max-w-sm">
                    <span className="text-xs font-semibold text-gray-600">Category</span>
                    <select
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        value={categoryId}
                        onChange={(e) => setCategoryId(e.target.value)}
                    >
                        <option value="">Select a category</option>
                        {categories.map((category) => (
                            <option key={category.categoryId} value={category.categoryId}>
                                {category.categoryName}
                            </option>
                        ))}
                    </select>
                </label>

                <form onSubmit={handleAddRole} className="flex flex-wrap items-end gap-2">
                    <label className="flex flex-col gap-1">
                        <span className="text-xs font-semibold text-gray-600">Role name</span>
                        <input
                            value={roleName}
                            onChange={(e) => setRoleName(e.target.value)}
                            className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-64"
                            placeholder="Cleaner"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={saving || !categoryId}
                        className="px-3 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white disabled:opacity-50"
                    >
                        {saving ? "Saving..." : "Add role"}
                    </button>
                </form>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                            <tr>
                                <th className="px-4 py-2">Role</th>
                                <th className="px-4 py-2">Category</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td className="px-4 py-3 text-gray-500" colSpan={2}>Loading roles...</td></tr>
                            )}
                            {!loading && roles.length === 0 && (
                                <tr><td className="px-4 py-3 text-gray-500" colSpan={2}>No roles for this category yet.</td></tr>
                            )}
                            {!loading && roles.map((role) => (
                                <tr key={role.staffRoleId} className="border-t border-gray-100">
                                    <td className="px-4 py-2">{role.roleName}</td>
                                    <td className="px-4 py-2">{role.categoryName || categories.find((c) => String(c.categoryId) === String(role.categoryId))?.categoryName || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StaffRoleMaster;
